import { StyledLoadingSpinner } from 'baseui/button'
import { StyledTable, SortableHeadCell, StyledBodyCell } from 'baseui/table-grid'
import { withStyle } from 'baseui'
import { useTranslation } from 'react-i18next'
import useContractsTable from './useContractsTable'
import useContractsTableStyles from './useContractsTableStyles'
import { getColumnDefinitions } from './columnDefinitions'
import { Contract } from '../types'

interface ContractsTableProps {
  businessType: string
  getContracts: () => void
  formInitUrl: string
}

const Table = withStyle(StyledTable, {
  alignContent: 'start',
})

const ContractsTable = ({ businessType, getContracts, formInitUrl }: ContractsTableProps) => {
  const { t } = useTranslation()
  const { sortedContracts, isLoading, sort, handleSort } = useContractsTable(businessType)
  const columns = getColumnDefinitions(businessType, t, getContracts, formInitUrl)
  const {
    headCellOverrides,
    loadingStyle,
    tableHeight,
    tableRow,
    headingDisplay,
    cellStyle,
    emptyStateStyle,
  } = useContractsTableStyles()

  if (isLoading) {
    return (
      <div className={loadingStyle}>
        <StyledLoadingSpinner />
      </div>
    )
  }

  const gridTemplate = columns.map(col => col.width).join(' ')

  return (
    <div className={tableHeight}>
      <Table role="grid" $gridTemplateColumns={gridTemplate}>
        <div role="row" className={headingDisplay}>
          {columns.map(col => (
            <SortableHeadCell
              key={col.key}
              title={col.label}
              direction={sort.key === col.key ? sort.direction : null}
              onSort={() => col.sortable && handleSort(col.key)}
              disabled={!col.sortable}
              overrides={headCellOverrides}
            />
          ))}
        </div>

        {sortedContracts.length === 0 ? (
          <div className={emptyStateStyle}>
            {t('contractsTable:no_contracts_found')}
          </div>
        ) : (
          sortedContracts.map((contract: Contract, index: number) => {
            const striped = index % 2 === 0
            return (
              <div role="row" className={tableRow} key={contract.id}>
                {columns.map(col => (
                  <StyledBodyCell key={col.key} className={cellStyle} $striped={striped}>
                    {col.render
                      ? col.render(contract[col.key], contract)
                      : col.format
                        ? col.format(contract[col.key])
                        : String(contract[col.key] ?? '-')}
                  </StyledBodyCell>
                ))}
              </div>
            )
          })
        )}
      </Table>
    </div>
  )
}

export default ContractsTable
